import {
  CURVE_SEGMENTS,
  DIAGRAM_PITCH_HEIGHT,
  DIAGRAM_PITCH_LABELS,
  DIAGRAM_SMALL_FACTOR,
  DIAGRAM_TEAM,
  arrowGeometry,
  arrowLastStepIndex,
  curvePoint,
  diagramSteps,
  flattenPolyline,
  flattenSmooth,
  makeFlatPath,
  roundedPolyline,
  smoothPath,
  type ArrowGeometry,
  type DiagramArrowKind,
  type DiagramElement,
  type DiagramPitch,
  type DiagramTeam,
  type DiagramZoneColor,
  type FlatPath,
  type TrainingDiagram,
} from '@ukinfo/shared';
import { useId } from 'react';
import { cn } from '../ui';

/**
 * Skizze einer Uebung als SVG - nur Anzeige.
 *
 * Die Koordinaten der Skizze sind Prozent des Spielfelds (0 links/oben,
 * 100 rechts/unten). Intern rechnet die Zeichnung mit einer Breite von 100
 * Einheiten; die Hoehe folgt dem Feld. Das SVG skaliert ueber die viewBox auf
 * die Breite des Containers und druckt ohne Zutun sauber.
 */

/** Meter eines Spielfelds in Zeichnungseinheiten (Breite 68 m = 100 Einheiten). */
const M = 100 / 68;
export const PADDING = 4;

/** Hoehe des Feldes bei Breite 100: ganzes Feld 105 m, halbes 52,5 m, freie Flaeche 5:4. */
export const PITCH_HEIGHT = DIAGRAM_PITCH_HEIGHT;

const GRASS = '#4f9d52';
const GRASS_DARK = '#468f49';
const LINE = 'rgba(255,255,255,0.85)';
export const ARROW = '#fef08a';
/** Positionswechsel: helles Tuerkis - neben dem Gelb der anderen Pfeile und auf dem Rasen gut zu erkennen. */
export const SWITCH_COLOR = '#a5f3fc';
/** Masslinie: Weiss mit dunklem Schatten, damit sie sich von den Feldlinien abhebt. */
export const MEASURE_COLOR = '#ffffff';

interface TeamStyle {
  fill: string;
  /** Farbe der Beschriftung - gegen `fill` gut lesbar. */
  text: string;
  name: string;
  /** Rand des Spielers; bei C und D zusaetzlich gestrichelt beziehungsweise gepunktet. */
  rim: string;
  rimWidth: number;
  /** Strichart des Randes bezogen auf einen Umfang von 28 Einheiten (`pathLength`); ohne Angabe durchgezogen. */
  rimDash?: string;
}

/**
 * Farben der Mannschaften. A, B und N sind Rot, Blau und Gelb mit weissem Rand.
 * C und D sind Weiss und Schwarz: Sie unterscheiden sich in Helligkeit statt Farbton
 * und bleiben damit auch bei Rot-Gruen-Schwaeche, im Graustufendruck und gegen den
 * gruenen Rasen eindeutig. Dazu kommt die Strichart des Randes - C gestrichelt mit
 * dunklem, D gepunktet mit weissem Rand -, damit sie auch ohne Farbe auseinanderfallen.
 */
export const TEAM_STYLE: Record<DiagramTeam, TeamStyle> = {
  A: { fill: '#dc2626', text: '#ffffff', name: 'Team A', rim: '#ffffff', rimWidth: 0.6 },
  B: { fill: '#2563eb', text: '#ffffff', name: 'Team B', rim: '#ffffff', rimWidth: 0.6 },
  N: { fill: '#facc15', text: '#1f2937', name: 'Neutral', rim: '#ffffff', rimWidth: 0.6 },
  C: { fill: '#ffffff', text: '#111827', name: 'Team C', rim: '#111827', rimWidth: 0.8, rimDash: '1.2 0.8' },
  D: { fill: '#111827', text: '#ffffff', name: 'Team D', rim: '#ffffff', rimWidth: 1, rimDash: '0.01 1.99' },
};

/** Rand eines Spielers als SVG-Eigenschaften. */
function rimProps(style: TeamStyle, widthFactor = 1): { stroke: string; strokeWidth: number; pathLength?: number; strokeDasharray?: string; strokeLinecap?: 'round' } {
  const strokeWidth = style.rimWidth * widthFactor;
  return style.rimDash
    ? { stroke: style.rim, strokeWidth, pathLength: 28, strokeDasharray: style.rimDash, strokeLinecap: 'round' }
    : { stroke: style.rim, strokeWidth };
}

/** Kleines Teamsymbol fuer Legende und Werkzeugknoepfe: der Spieler mit seinem Rand. */
export function TeamSwatch({ team, className }: { team: DiagramTeam; className?: string }): JSX.Element {
  const style = TEAM_STYLE[team];
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-team-swatch={team}>
      {/* Grauer Ring darum: weisse und schwarze Spieler bleiben auf jedem Hintergrund sichtbar. */}
      <circle cx="12" cy="12" r="10.8" fill="none" stroke="#94a3b8" strokeWidth="1.2" />
      <circle cx="12" cy="12" r="8" fill={style.fill} {...rimProps(style, 2.4)} />
    </svg>
  );
}

export const GOAL_WIDTH = { MINI: 5, YOUTH: 8, FULL: 11 } as const;
export const GOAL_DEPTH = 2.4;
const PLAYER_RADIUS = 3.4;
/** Mitte der Kontur eines Koordinationsrings - Durchmesser samt Strich etwa wie ein Spieler. */
const RING_RADIUS = 3.1;

/**
 * Groesse eines Elements als Faktor: Symbolgroesse der Skizze, bei `size: 'S'` zusaetzlich
 * kleiner (Spieler, Ball, Huetchen, Stange, Ring, Huerde). Tore folgen nur der Skizze,
 * Zonen und Pfeile haben keinen Faktor (Pfeile: `arrowScale`).
 */
export function elementScale(element: DiagramElement, symbolScale = 1): number {
  if (element.type === 'zone' || element.type === 'arrow' || element.type === 'measure') return 1;
  // Text bleibt lesbar: er folgt der Symbolgroesse nur maessig (wie die Pfeile), `size: 'S'` macht ihn kleiner.
  if (element.type === 'text') return Math.round(arrowScale(symbolScale) * (element.size === 'S' ? DIAGRAM_SMALL_FACTOR : 1) * 1000) / 1000;
  const small = element.type !== 'goal' && element.size === 'S';
  return Math.round(symbolScale * (small ? DIAGRAM_SMALL_FACTOR : 1) * 1000) / 1000;
}

/** Pfeile folgen der Symbolgroesse nur maessig: Normal 1, Mittel 0,9, Klein 0,8. */
export function arrowScale(symbolScale = 1): number {
  return (1 + symbolScale) / 2;
}

/** Zeichenmass mal Faktor, auf zwei Stellen gerundet (kein Gleitkomma-Rauschen im SVG). */
const sc = (value: number, factor: number): number => Math.round(value * factor * 100) / 100;

/** SVG-Transformation, die ein Symbol um seine Mitte skaliert; bei Faktor 1 entfaellt sie. */
function scaleAbout(cx: number, cy: number, factor: number): string | undefined {
  return factor === 1 ? undefined : `translate(${num(cx)} ${num(cy)}) scale(${factor}) translate(${num(-cx)} ${num(-cy)})`;
}

/** Reihenfolge der Ebenen: Flaeche unten, Ball oben, Text ganz oben (er soll lesbar bleiben). */
export const LAYER: Record<DiagramElement['type'], number> = { zone: 0, goal: 1, ring: 2, hurdle: 3, cone: 4, pole: 4, arrow: 5, measure: 5.5, player: 6, ball: 7, text: 8 };

/** Namen der Ausstattung in Legende und Editor. */
export const EQUIPMENT_LABELS = { pole: 'Slalomstange', ring: 'Koordinationsring', hurdle: 'Hürde' } as const;

export const ARROW_LABELS: Record<DiagramArrowKind, string> = {
  PASS: 'Pass',
  RUN: 'Laufweg',
  DRIBBLE: 'Dribbling',
  SHOT: 'Torschuss',
  LOFT: 'Hoher Ball',
  SWITCH: 'Positionswechsel',
};

/** Name des Passes mit "Hinwechseln" in Legende und Editor. */
export const FOLLOW_LABEL = 'Passgeber läuft nach';

/** Name der Masslinie in Legende und Editor. */
export const MEASURE_LABEL = 'Abstand';

/** Name der Blickrichtung (Keil am Spieler) in Legende und Editor. */
export const FACING_LABEL = 'Blickrichtung';

/** Kurzer Text fuer Screenreader: was ist auf der Skizze zu sehen. */
export function describeDiagram(diagram: TrainingDiagram): string {
  const count = (type: DiagramElement['type']): number => diagram.elements.filter((element) => element.type === type).length;
  const parts = [
    count('player') ? `${count('player')} Spieler` : null,
    count('cone') ? `${count('cone')} Hütchen` : null,
    count('pole') ? `${count('pole')} ${count('pole') === 1 ? 'Slalomstange' : 'Slalomstangen'}` : null,
    count('ring') ? `${count('ring')} ${count('ring') === 1 ? 'Ring' : 'Ringe'}` : null,
    count('hurdle') ? `${count('hurdle')} ${count('hurdle') === 1 ? 'Hürde' : 'Hürden'}` : null,
    count('goal') ? `${count('goal')} Tore` : null,
    count('arrow') ? `${count('arrow')} Pfeile` : null,
    count('measure') ? `${count('measure')} ${count('measure') === 1 ? 'Maßlinie' : 'Maßlinien'}` : null,
    count('text') ? `${count('text')} ${count('text') === 1 ? 'Text' : 'Texte'}` : null,
  ].filter(Boolean);
  return `Skizze, ${DIAGRAM_PITCH_LABELS[diagram.pitch]}${parts.length ? `: ${parts.join(', ')}` : ''}`;
}

/** Spielfeldlinien eines ganzen oder halben Feldes nach den Massen des Grossfelds. */
function PitchLines({ pitch, height }: { pitch: 'FULL' | 'HALF'; height: number }): JSX.Element {
  const w = 100;
  const boxW = 40.32 * M;
  const boxD = 16.5 * M;
  const smallW = 18.32 * M;
  const smallD = 5.5 * M;
  const spot = 11 * M;
  const radius = 9.15 * M;
  const props = { fill: 'none', stroke: LINE, strokeWidth: 0.55 } as const;
  // Strafraum, Fuenfmeterraum, Elfmeterpunkt und Bogen am oberen Ende; unten gespiegelt.
  const end = (top: boolean): JSX.Element => {
    const sign = top ? 1 : -1;
    const base = top ? 0 : height;
    const arcDx = Math.sqrt(radius * radius - (boxD - spot) * (boxD - spot));
    return (
      <g>
        <rect x={(w - boxW) / 2} y={top ? base : base - boxD} width={boxW} height={boxD} {...props} />
        <rect x={(w - smallW) / 2} y={top ? base : base - smallD} width={smallW} height={smallD} {...props} />
        <circle cx={w / 2} cy={base + sign * spot} r={0.6} fill={LINE} />
        <path
          d={`M ${w / 2 - arcDx} ${base + sign * boxD} A ${radius} ${radius} 0 0 ${top ? 0 : 1} ${w / 2 + arcDx} ${base + sign * boxD}`}
          {...props}
        />
      </g>
    );
  };
  return (
    <g>
      <rect x={0} y={0} width={w} height={height} {...props} strokeWidth={0.8} />
      {end(true)}
      {pitch === 'FULL' ? (
        <>
          {end(false)}
          <line x1={0} y1={height / 2} x2={w} y2={height / 2} {...props} />
          <circle cx={w / 2} cy={height / 2} r={radius} {...props} />
          <circle cx={w / 2} cy={height / 2} r={0.6} fill={LINE} />
        </>
      ) : (
        // Halbes Feld: unten liegt die Mittellinie, der Mittelkreis ragt als Halbkreis herein.
        <path d={`M ${w / 2 - radius} ${height} A ${radius} ${radius} 0 0 1 ${w / 2 + radius} ${height}`} {...props} />
      )}
    </g>
  );
}

/** Geometrie eines Pfeils in Zeichnungseinheiten: y in Prozent wird mit der Feldhoehe gestreckt. */
export function drawingGeometry(
  arrow: {
    from: readonly [number, number];
    to: readonly [number, number];
    via?: readonly [number, number] | undefined;
    points?: readonly (readonly [number, number])[] | undefined;
    smooth?: boolean | undefined;
  },
  height: number,
): ArrowGeometry {
  const { from, to, via, points, smooth } = arrowGeometry(arrow);
  const scale = (point: { x: number; y: number }): { x: number; y: number } => ({ x: point.x, y: (point.y / 100) * height });
  const geometry: ArrowGeometry = { from: scale(from), to: scale(to), via: via ? scale(via) : null };
  if (points) {
    geometry.points = points.map(scale);
    if (smooth) geometry.smooth = true;
  }
  return geometry;
}

const num = (value: number): string => value.toFixed(2);

/** Eckpunkte des Linienzugs (Anfang, Knickpunkte, Ende) oder `null` bei Pfeilen ohne Knickpunkte; die Geometrie ist schon in Zeichnungseinheiten. */
function polylineVertices(geometry: ArrowGeometry): { x: number; y: number }[] | null {
  return geometry.points && geometry.points.length > 0 ? [geometry.from, ...geometry.points, geometry.to] : null;
}

/** Pfad eines Pfeils ohne Verzierung: Gerade, quadratische Kurve, Linienzug mit leicht gerundeten Ecken oder glatte Kurve durch alle Punkte. */
export function arrowGuidePath(geometry: ArrowGeometry): string {
  const { from, to, via } = geometry;
  const vertices = polylineVertices(geometry);
  if (vertices && geometry.smooth) {
    // Die Pfeilspitze (marker-end, orient=auto) richtet sich nach der Tangente am Ende des letzten Bezierstuecks.
    const { start, ops } = smoothPath(vertices);
    return `M ${num(start.x)} ${num(start.y)} ${ops.map((op) => `C ${num(op.c1.x)} ${num(op.c1.y)} ${num(op.c2.x)} ${num(op.c2.y)} ${num(op.to.x)} ${num(op.to.y)}`).join(' ')}`;
  }
  if (vertices) {
    const { start, ops } = roundedPolyline(vertices);
    return `M ${num(start.x)} ${num(start.y)} ${ops
      .map((op) => (op.kind === 'L' ? `L ${num(op.to.x)} ${num(op.to.y)}` : `Q ${num(op.control.x)} ${num(op.control.y)} ${num(op.to.x)} ${num(op.to.y)}`))
      .join(' ')}`;
  }
  return via
    ? `M ${num(from.x)} ${num(from.y)} Q ${num(via.x)} ${num(via.y)} ${num(to.x)} ${num(to.y)}`
    : `M ${num(from.x)} ${num(from.y)} L ${num(to.x)} ${num(to.y)}`;
}

/** Der Linienzug als abgetasteter Weg (gerundete Ecken, mit `smooth` die glatte Kurve); `null` ohne Knickpunkte. */
function flatPathOf(geometry: ArrowGeometry): FlatPath | null {
  const vertices = polylineVertices(geometry);
  if (!vertices) return null;
  return makeFlatPath(geometry.smooth ? flattenSmooth(vertices) : flattenPolyline(vertices));
}

/** Angenaeherte Laenge in Zeichnungseinheiten (die Geometrie ist schon in Einheiten). */
function drawingLength(geometry: ArrowGeometry): number {
  const flat = flatPathOf(geometry);
  if (flat) return flat.length;
  let length = 0;
  let last = geometry.from;
  const segments = geometry.via ? CURVE_SEGMENTS : 1;
  for (let step = 1; step <= segments; step += 1) {
    const next = curvePoint(geometry, step / segments);
    length += Math.hypot(next.x - last.x, next.y - last.y);
    last = next;
  }
  return length;
}

/** Seitlicher Abstand der Begleitlinie beim Hinwechseln, in Zeichnungseinheiten (Normal; folgt der Pfeilgroesse). */
const FOLLOW_OFFSET = 3.2;
/** So weit endet die Begleitlinie vor dem Ende des Passes: ihre kleine Spitze bleibt neben dem Schaft, nicht neben der Pfeilspitze des Passes. */
const FOLLOW_END_GAP = 1.2;

/**
 * Begleitlinie eines Passes mit "Hinwechseln" (`follow`): der Weg des Passes, seitlich versetzt - rechts in Laufrichtung, die
 * Schrittnummern stehen links. Sie folgt dem Pass auch bei Bogen, Knicken und weichen Kurven (der abgetastete Weg wird Punkt fuer
 * Punkt zur Seite geschoben, die Ecken mit gemittelter Richtung). Gerade Pfeile geben eine gerade Linie. `null` bei einem Pfeil ohne Laenge.
 */
export function followLinePath(geometry: ArrowGeometry, factor = 1): string | null {
  const flat = flatPathOf(geometry) ?? makeFlatPath(geometry.via ? Array.from({ length: CURVE_SEGMENTS + 1 }, (_, index) => curvePoint(geometry, index / CURVE_SEGMENTS)) : [geometry.from, geometry.to]);
  const stop = flat.length - sc(FOLLOW_END_GAP, factor);
  if (stop <= 0.5) return null;
  /** Punkt bei Weglaenge `along` (zwischen den Abtastpunkten gerade verbunden). */
  const at = (along: number): { x: number; y: number } => {
    let index = 1;
    while (index < flat.points.length - 1 && flat.offsets[index]! < along) index += 1;
    const a = flat.points[index - 1]!;
    const b = flat.points[index]!;
    const span = flat.offsets[index]! - flat.offsets[index - 1]! || 1;
    const f = Math.min(1, Math.max(0, (along - flat.offsets[index - 1]!) / span));
    return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
  };
  const points = [at(0), ...flat.points.filter((_, index) => flat.offsets[index]! > 0.01 && flat.offsets[index]! < stop - 0.01), at(stop)];
  // Zwei gleiche Punkte hintereinander (eine Schleife, die auf sich selbst liegt) haben keine Richtung.
  const distinct = points.filter((point, index) => index === 0 || Math.hypot(point.x - points[index - 1]!.x, point.y - points[index - 1]!.y) > 0.01);
  if (distinct.length < 2) return null;
  const normals = distinct.slice(1).map((point, index) => {
    const before = distinct[index]!;
    const length = Math.hypot(point.x - before.x, point.y - before.y);
    return { x: -(point.y - before.y) / length, y: (point.x - before.x) / length };
  });
  const gap = sc(FOLLOW_OFFSET, factor);
  const shifted = distinct.map((point, index) => {
    const a = normals[index - 1];
    const b = normals[index];
    const first = a ?? b!;
    const second = b ?? a!;
    let nx = first.x + second.x;
    let ny = first.y + second.y;
    const norm = Math.hypot(nx, ny);
    if (norm < 1e-6) {
      nx = second.x;
      ny = second.y;
    } else {
      nx /= norm;
      ny /= norm;
    }
    // Ecken: den Versatz verlaengern, damit der Abstand zur Linie gleich bleibt (begrenzt).
    const cos = Math.max(nx * second.x + ny * second.y, 0.62);
    return `${num(point.x + (nx * gap) / cos)} ${num(point.y + (ny * gap) / cos)}`;
  });
  return `M ${shifted.join(' L ')}`;
}

/** Teilkurve von t = 0 bis `t` (de Casteljau); bei der Geraden die Teilstrecke, beim Linienzug der Anteil `t` der Weglaenge. */
function partialPath(geometry: ArrowGeometry, t: number): string {
  const { from, via } = geometry;
  const flat = flatPathOf(geometry);
  if (flat) {
    const reach = t * flat.length;
    const kept: string[] = [];
    flat.points.forEach((point, index) => {
      if (flat.offsets[index]! <= reach) {
        kept.push(`${num(point.x)} ${num(point.y)}`);
        return;
      }
      const before = flat.points[index - 1];
      if (!before || flat.offsets[index - 1]! >= reach) return;
      const f = (reach - flat.offsets[index - 1]!) / (flat.offsets[index]! - flat.offsets[index - 1]!);
      kept.push(`${num(before.x + (point.x - before.x) * f)} ${num(before.y + (point.y - before.y) * f)}`);
    });
    return `M ${kept.join(' L ')}`;
  }
  const end = curvePoint(geometry, t);
  if (!via) return `M ${num(from.x)} ${num(from.y)} L ${num(end.x)} ${num(end.y)}`;
  const control = { x: from.x + (via.x - from.x) * t, y: from.y + (via.y - from.y) * t };
  return `M ${num(from.x)} ${num(from.y)} Q ${num(control.x)} ${num(control.y)} ${num(end.x)} ${num(end.y)}`;
}

/** Wellenlinie fuer das Dribbling: sinusfoermig entlang der Linie (gerade, Kurve oder Linienzug) bis kurz vor die Spitze, dann ohne Welle. */
function wavePath(geometry: ArrowGeometry, factor = 1): string {
  // Die Kurve wird abgetastet und ueber die Bogenlaenge durchlaufen: Wellenlaenge und
  // Amplitude sind auf der Kurve so gleichmaessig wie auf der Geraden, der Versatz
  // steht senkrecht zur Laufrichtung. Ein Linienzug liefert seine Abtastung selbst (ueber alle Knicke).
  const flat = flatPathOf(geometry);
  const samples = geometry.via ? 96 : 1;
  const table: { x: number; y: number; s: number }[] = [];
  let travelled = 0;
  if (flat) {
    flat.points.forEach((point, index) => table.push({ x: point.x, y: point.y, s: flat.offsets[index]! }));
    travelled = flat.length;
  } else {
    for (let index = 0; index <= samples; index += 1) {
      const point = curvePoint(geometry, index / samples);
      const last = table[index - 1];
      if (last) travelled += Math.hypot(point.x - last.x, point.y - last.y);
      table.push({ x: point.x, y: point.y, s: travelled });
    }
  }
  const length = travelled;
  /** Punkt und Laufrichtung bei Bogenlaenge `along`. */
  const at = (along: number): { x: number; y: number; ux: number; uy: number } => {
    let index = 1;
    while (index < table.length - 1 && table[index]!.s < along) index += 1;
    const a = table[index - 1]!;
    const b = table[index]!;
    const span = b.s - a.s || 1;
    const f = Math.min(1, Math.max(0, (along - a.s) / span));
    return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, ux: (b.x - a.x) / span, uy: (b.y - a.y) / span };
  };

  const straight = Math.min(4 * factor, length / 2);
  const waveLength = length - straight;
  const periods = Math.max(1, Math.round(waveLength / 6));
  const amplitude = 1.3 * factor;
  const steps = Math.max(24, Math.ceil(length * 3));
  const points: string[] = [];
  for (let step = 0; step <= steps; step += 1) {
    const t = step / steps;
    const base = at(t * waveLength);
    const offset = amplitude * Math.sin(t * periods * 2 * Math.PI);
    points.push(`${(base.x - base.uy * offset).toFixed(2)} ${(base.y + base.ux * offset).toFixed(2)}`);
  }
  // Das letzte Stueck folgt der Linie ohne Welle; bei Kurven in kleinen Schritten, damit die Spitze in Endrichtung zeigt.
  const tail = geometry.via || flat ? 4 : 1;
  for (let step = 1; step <= tail; step += 1) {
    const base = at(waveLength + (straight * step) / tail);
    points.push(`${base.x.toFixed(2)} ${base.y.toFixed(2)}`);
  }
  return `M ${points.join(' L ')}`;
}

/** Anteil der Strecke, auf dem die Mitte des Torschusspfeils aufgeschnitten wird. */
function shotCut(length: number, factor = 1): number {
  return Math.max(0.2, 1 - (4.5 * factor) / length);
}

/** Mitte des Pfeils und Drehwinkel (Grad) fuer das Bogensymbol des hohen Balls; der Bogen bleibt immer aufrecht (nie kopfstehend). */
function loftMiddle(geometry: ArrowGeometry): { x: number; y: number; angle: number } {
  const { from, to } = geometry;
  const at = curvePoint(geometry, 0.5);
  // Bei t = 0,5 zeigt die Tangente der quadratischen Kurve genau von Anfang nach Ende.
  return { x: at.x, y: at.y, angle: measureLabelAngle(from, to) };
}

function Arrow({
  kind,
  from,
  to,
  via,
  points,
  smooth,
  follow,
  markerId,
  height,
  factor,
}: {
  kind: DiagramArrowKind;
  from: [number, number];
  to: [number, number];
  via?: [number, number] | undefined;
  points?: [number, number][] | undefined;
  /** Mit Knickpunkten: glatte Kurve durch alle Punkte. */
  smooth?: boolean | undefined;
  /** Nur beim Pass: "Hinwechseln" - eine duenne gepunktete Begleitlinie zeigt, dass der Passgeber nachlaeuft. */
  follow?: boolean | undefined;
  markerId: string;
  height: number;
  /** Strichstaerke, Welle und Pfeilspitze gegenueber Normal (`arrowScale`). */
  factor: number;
}): JSX.Element | null {
  const knicks = points?.length ?? 0;
  if (from[0] === to[0] && from[1] === to[1] && knicks === 0) return null;
  const geometry = drawingGeometry({ from, to, via, points, smooth }, height);
  const marker = `url(#${markerId}${kind === 'SHOT' ? 'S' : kind === 'SWITCH' ? 'W' : ''})`;
  const common = { fill: 'none', stroke: ARROW, strokeLinecap: 'round', strokeLinejoin: 'round', markerEnd: marker } as const;
  const path = arrowGuidePath(geometry);
  const line = { x1: geometry.from.x, y1: geometry.from.y, x2: geometry.to.x, y2: geometry.to.y };
  const width = sc(0.9, factor);
  const dash = `${sc(2.6, factor)} ${sc(2, factor)}`;
  const cut = shotCut(drawingLength(geometry), factor);
  // Gerade Pfeile bleiben Linien; gebogene und Knickpfeile sind Pfade - alle mit Spitze in Endrichtung.
  const bent = Boolean(via) || knicks > 0;
  const stroke = (props: Record<string, unknown>): JSX.Element => (bent ? <path d={path} {...props} /> : <line {...line} {...props} />);
  const middle = kind === 'LOFT' ? loftMiddle(geometry) : null;
  const followLine = kind === 'PASS' && follow === true ? followLinePath(geometry, factor) : null;
  return (
    <g data-element="arrow" data-kind={kind} data-curved={via ? 'true' : undefined} data-knicks={knicks > 0 ? knicks : undefined} data-smooth={knicks > 0 && smooth ? 'true' : undefined} data-follow={followLine ? 'true' : undefined}>
      {kind === 'PASS' && stroke({ strokeWidth: width, ...common })}
      {/* Hinwechseln: Begleitlinie im Stil des Positionswechsels (tuerkis, gepunktet, kleine offene Spitze), seitlich neben dem Pass. */}
      {followLine && (
        <path data-testid="follow-line" d={followLine} fill="none" stroke={SWITCH_COLOR} strokeWidth={sc(0.75, factor)} strokeDasharray={`0.01 ${sc(1.7, factor)}`} strokeLinecap="round" strokeLinejoin="round" markerEnd={`url(#${markerId}W)`} />
      )}
      {kind === 'RUN' && stroke({ strokeWidth: width, strokeDasharray: dash, ...common })}
      {kind === 'DRIBBLE' && <path d={wavePath(geometry, factor)} strokeWidth={width} {...common} />}
      {/* Positionswechsel: duenn und gepunktet in Tuerkis, mit kleiner offener Spitze - klar anders als der gestrichelte Laufweg. */}
      {kind === 'SWITCH' && stroke({ ...common, stroke: SWITCH_COLOR, strokeWidth: sc(0.75, factor), strokeDasharray: `0.01 ${sc(1.7, factor)}` })}
      {/* Hoher Ball: Strich-Punkt-Linie mit einem kleinen Bogen in der Mitte (der Ball fliegt darueber) - weder Pass noch Laufweg zu verwechseln, auch grau gedruckt. */}
      {kind === 'LOFT' && (
        <>
          {stroke({ strokeWidth: width, strokeDasharray: `${sc(3.4, factor)} ${sc(1.3, factor)} 0.01 ${sc(1.3, factor)}`, ...common })}
          {middle && (
            <g data-testid="loft-arc" transform={`translate(${num(middle.x)} ${num(middle.y)}) rotate(${middle.angle})`}>
              <path d={`M ${-sc(3.3, factor)} ${sc(1.6, factor)} Q 0 ${-sc(5.2, factor)} ${sc(3.3, factor)} ${sc(1.6, factor)}`} fill="none" stroke="#1f2937" strokeWidth={sc(2.2, factor)} strokeLinecap="round" opacity={0.4} />
              <path d={`M ${-sc(3.3, factor)} ${sc(1.6, factor)} Q 0 ${-sc(5.2, factor)} ${sc(3.3, factor)} ${sc(1.6, factor)}`} fill="none" stroke={ARROW} strokeWidth={sc(1, factor)} strokeLinecap="round" />
            </g>
          )}
        </>
      )}
      {kind === 'SHOT' && (
        <>
          {/* Doppelt: dicke helle Linie, in der Mitte mit Rasenfarbe aufgeschnitten
              (bis kurz vor die Spitze, damit die Pfeilspitze voll bleibt). */}
          {stroke({ strokeWidth: sc(2.2, factor), ...common })}
          {bent ? (
            <path d={partialPath(geometry, cut)} strokeWidth={sc(0.8, factor)} stroke={GRASS} fill="none" />
          ) : (
            <line
              x1={geometry.from.x}
              y1={geometry.from.y}
              x2={geometry.from.x + (geometry.to.x - geometry.from.x) * cut}
              y2={geometry.from.y + (geometry.to.y - geometry.from.y) * cut}
              strokeWidth={sc(0.8, factor)}
              stroke={GRASS}
              fill="none"
            />
          )}
        </>
      )}
    </g>
  );
}

/**
 * Drehwinkel (Grad) der Beschriftung einer Masslinie: parallel zur Linie, aber nie kopfstehend -
 * der Text liest sich immer von links nach rechts (Bereich -90 bis 90). Gerechnet in Zeichnungseinheiten.
 */
export function measureLabelAngle(from: { x: number; y: number }, to: { x: number; y: number }): number {
  let angle = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
  if (angle > 90) angle -= 180;
  else if (angle <= -90) angle += 180;
  return Math.round(angle * 10) / 10 + 0;
}

/** Masslinie: duenne Linie mit Querstrichen an beiden Enden, die Beschriftung sitzt mittig in einem dunklen Kasten. */
function MeasureLine({
  from,
  to,
  label,
  height,
  factor,
}: {
  from: readonly [number, number];
  to: readonly [number, number];
  label?: string | undefined;
  height: number;
  factor: number;
}): JSX.Element | null {
  if (from[0] === to[0] && from[1] === to[1]) return null;
  const a = { x: from[0], y: (from[1] / 100) * height };
  const b = { x: to[0], y: (to[1] / 100) * height };
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  const ux = (b.x - a.x) / length;
  const uy = (b.y - a.y) / length;
  // Querstrich senkrecht zur Linie.
  const tick = sc(1.4, factor);
  const ends = [a, b].map((end) => `M ${num(end.x - uy * tick)} ${num(end.y + ux * tick)} L ${num(end.x + uy * tick)} ${num(end.y - ux * tick)}`).join(' ');
  const body = `M ${num(a.x)} ${num(a.y)} L ${num(b.x)} ${num(b.y)} ${ends}`;
  const text = label?.trim();
  const boxW = text ? sc(text.length * 1.45 + 2.4, factor) : 0;
  const boxH = sc(4, factor);
  const angle = measureLabelAngle(a, b);
  // Passt der Kasten nicht auf die Linie, ohne sie ganz zu verdecken, sitzt er knapp darueber.
  const lift = text && length < boxW + 5 ? -(boxH / 2 + 1) : 0;
  return (
    <g data-element="measure" data-label={text || undefined} data-angle={text ? angle : undefined}>
      <path d={body} fill="none" stroke="#1f2937" strokeWidth={sc(1.2, factor)} strokeLinecap="round" opacity={0.4} />
      <path d={body} fill="none" stroke={MEASURE_COLOR} strokeWidth={sc(0.45, factor)} strokeLinecap="round" />
      {text && (
        <g transform={`translate(${num((a.x + b.x) / 2)} ${num((a.y + b.y) / 2)}) rotate(${angle}) translate(0 ${num(lift)})`}>
          <rect x={-boxW / 2} y={-boxH / 2} width={boxW} height={boxH} rx={sc(1.1, factor)} fill="rgba(15,23,42,0.78)" stroke={MEASURE_COLOR} strokeWidth={sc(0.25, factor)} />
          <text x={0} y={0} textAnchor="middle" dominantBaseline="central" fontSize={sc(2.6, factor)} fontWeight={700} fontFamily="system-ui, sans-serif" fill="#ffffff">
            {text}
          </text>
        </g>
      )}
    </g>
  );
}

function GoalShape({ x, y, size, rotation, factor }: { x: number; y: number; size: keyof typeof GOAL_WIDTH; rotation: number; factor: number }): JSX.Element {
  const width = GOAL_WIDTH[size];
  const scaled = scaleAbout(x, y, factor);
  return (
    <g
      data-element="goal"
      data-size={size}
      data-rotation={rotation}
      data-scale={scaled ? factor : undefined}
      transform={`rotate(${rotation} ${x} ${y})${scaled ? ` ${scaled}` : ''}`}
    >
      {/* Tor mit Oeffnung zum Feld: bei 0 Grad oben am Feldrand, der Rahmen ragt nach aussen. */}
      <rect x={x - width / 2} y={y - GOAL_DEPTH} width={width} height={GOAL_DEPTH} fill="rgba(255,255,255,0.25)" stroke="#ffffff" strokeWidth={0.7} />
    </g>
  );
}

/* ------------------------------------------------------------------ *
 * Zonen: Form und Farbe
 * ------------------------------------------------------------------ */

interface ZoneStyle {
  fill: string;
  stroke: string;
  strokeWidth: number;
  /** Strichart des Randes; ohne Angabe durchgezogen. */
  dash?: string;
  /** Kennung des Fuellmusters im `<defs>` (siehe `PitchSurface`); ohne Angabe nur die Farbflaeche. */
  pattern?: 'Zy' | 'Zr' | 'Zb' | 'Zo';
}

/**
 * Halbtransparente Flaeche mit kraeftigerem Rand. Jede Farbe hat zusaetzlich ein eigenes Fuellmuster und eine eigene
 * Strichart des Randes (Weiss gestrichelt wie immer, Gelb durchgezogen, Rot lang gestrichelt und schraffiert
 * entgegengesetzt, Blau gepunktet, Orange Strich-Punkt und kariert) - so bleiben die Zonen auch grau gedruckt und bei
 * Rot-Gruen-Schwaeche unterscheidbar. Weiss ohne Form ist die Zone, wie sie vor der Wahl immer aussah.
 */
export const ZONE_STYLE: Record<DiagramZoneColor, ZoneStyle> = {
  white: { fill: 'rgba(255,255,255,0.16)', stroke: 'rgba(255,255,255,0.75)', strokeWidth: 0.5, dash: '1.6 1.2' },
  yellow: { fill: 'rgba(250,204,21,0.3)', stroke: '#fde047', strokeWidth: 0.8, pattern: 'Zy' },
  red: { fill: 'rgba(220,38,38,0.34)', stroke: '#f87171', strokeWidth: 0.8, dash: '3.4 1.3', pattern: 'Zr' },
  blue: { fill: 'rgba(37,99,235,0.34)', stroke: '#93c5fd', strokeWidth: 0.8, dash: '0.01 1.5', pattern: 'Zb' },
  orange: { fill: 'rgba(249,115,22,0.32)', stroke: '#fdba74', strokeWidth: 0.8, dash: '3 1.1 0.01 1.1', pattern: 'Zo' },
};

/** Die Farben der Zonen einer Skizze (ohne Weiss und ohne Doppelte): fuer sie braucht `PitchSurface` Fuellmuster. */
export function zoneColorsOf(elements: readonly DiagramElement[]): DiagramZoneColor[] {
  const colors = new Set<DiagramZoneColor>();
  for (const element of elements) {
    if (element.type === 'zone' && element.color && element.color !== 'white') colors.add(element.color);
  }
  return [...colors];
}

/** Ecken des Dreiecks im Rechteck: die Spitze zeigt nach oben (0), rechts (90), unten (180) oder links (270). */
export function zoneTrianglePoints(x: number, y: number, w: number, h: number, rotation: 0 | 90 | 180 | 270 = 0): string {
  const corners: [number, number][] =
    rotation === 90
      ? [[x + w, y + h / 2], [x, y + h], [x, y]]
      : rotation === 180
        ? [[x + w / 2, y + h], [x, y], [x + w, y]]
        : rotation === 270
          ? [[x, y + h / 2], [x + w, y], [x + w, y + h]]
          : [[x + w / 2, y], [x + w, y + h], [x, y + h]];
  return corners.map(([px, py]) => `${num(px)},${num(py)}`).join(' ');
}

/** Umriss einer Zone (Rechteck, Ellipse im Rechteck oder Dreieck) mit den gegebenen Farb- und Strichangaben; die Hoehe ist die des Felds. */
export function ZoneOutline({ zone, height, ...paint }: { zone: Extract<DiagramElement, { type: 'zone' }>; height: number } & Record<string, unknown>): JSX.Element {
  const x = zone.x;
  const y = (zone.y / 100) * height;
  const w = zone.w;
  const h = (zone.h / 100) * height;
  switch (zone.shape ?? 'rect') {
    case 'circle':
      return <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} {...paint} />;
    case 'triangle':
      return <polygon points={zoneTrianglePoints(x, y, w, h, zone.rotation ?? 0)} {...paint} />;
    default:
      return <rect x={x} y={y} width={w} height={h} {...paint} />;
  }
}

/* ------------------------------------------------------------------ *
 * Text auf dem Feld
 * ------------------------------------------------------------------ */

/** Schriftgroesse eines Texts bei Faktor 1 in Zeichnungseinheiten (etwas groesser als die Spielernummer). */
const TEXT_FONT_SIZE = 3.4;

/** Breite eines Zeichens in Schriftgroessen: gerundet nach oben, damit die Schaetzung den Text lieber zu breit als zu schmal annimmt. */
function charWidth(char: string): number {
  if ('il.,:;!|\'`'.includes(char)) return 0.34;
  if ('fjrtI()[]{}/-– '.includes(char)) return 0.46;
  if ('mwMW@%'.includes(char)) return 0.92;
  if (/[A-ZÄÖÜ]/.test(char)) return 0.72;
  return 0.6;
}

/** Geschaetzte Breite eines Texts in Zeichnungseinheiten (fette Systemschrift). */
export function textWidth(text: string, fontSize: number): number {
  return [...text].reduce((sum, char) => sum + charWidth(char), 0) * fontSize;
}

export interface TextLayout {
  /** Mitte der Beschriftung in Zeichnungseinheiten - so geklemmt, dass sie ganz im Feld liegt. */
  cx: number;
  cy: number;
  /** Geschaetzter Kasten um den Text (samt Halo). */
  w: number;
  h: number;
  fontSize: number;
}

/** Breiteste Beschriftung in Zeichnungseinheiten: laengere Texte werden kleiner geschrieben, damit sie nie ueber den Feldrand ragen. */
const TEXT_MAX_WIDTH = 96;

/**
 * Lage und Groesse eines Texts: Mitte bei `x`/`y`, aber verschoben, damit der geschaetzte Kasten nicht ueber den
 * Feldrand hinausragt; ein Text, der auch dann nicht passt, wird verkleinert.
 */
export function textLayout(element: Extract<DiagramElement, { type: 'text' }>, height: number, symbolScale = 1): TextLayout {
  const factor = elementScale(element, symbolScale);
  let fontSize = TEXT_FONT_SIZE * factor;
  let w = textWidth(element.text, fontSize) + 2 * factor;
  if (w > TEXT_MAX_WIDTH) {
    fontSize = (fontSize * TEXT_MAX_WIDTH) / w;
    w = TEXT_MAX_WIDTH;
  }
  const h = fontSize * 1.3 + factor;
  const clampTo = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
  return {
    cx: clampTo(element.x, w / 2, 100 - w / 2),
    cy: clampTo((element.y / 100) * height, h / 2, height - h / 2),
    w,
    h,
    fontSize: Math.round(fontSize * 100) / 100,
  };
}

/** Keil der Blickrichtung am Rand eines Spielers: Farben gegen Mannschaftsfarbe und Rasen. */
function FacingNose({ cx, cy, facing, style }: { cx: number; cy: number; facing: number; style: TeamStyle }): JSX.Element {
  // Fuellung wie die Beschriftung des Spielers (gegen dessen Farbe lesbar), Rand in der Gegenfarbe - so hebt sich der Keil auch vom Rasen ab.
  const dark = style.text !== '#ffffff';
  return (
    <polygon
      data-nose={facing}
      transform={`rotate(${facing} ${num(cx)} ${num(cy)})`}
      points={`${num(cx)},${num(cy - PLAYER_RADIUS - 2.3)} ${num(cx - 1.9)},${num(cy - PLAYER_RADIUS + 0.7)} ${num(cx + 1.9)},${num(cy - PLAYER_RADIUS + 0.7)}`}
      fill={dark ? '#1f2937' : '#ffffff'}
      stroke={dark ? '#ffffff' : '#111827'}
      strokeWidth={0.45}
      strokeLinejoin="round"
    />
  );
}

/**
 * Ein Element der Skizze; der Editor zeichnet damit genau dasselbe wie die Anzeige.
 *
 * `scale` ist die Symbolgroesse der Skizze (`symbolScale`, ohne Angabe 1): Symbole
 * schrumpfen samt Rand und Beschriftung um ihre Mitte, Pfeile maessig, Zonen gar nicht.
 */
export function SketchElement({
  element,
  markerId,
  height,
  scale = 1,
  lift = 0,
}: {
  element: DiagramElement;
  markerId: string;
  height: number;
  scale?: number;
  /** Nur Baelle im Ablauf: Flughoehe 0 bis 1 eines hohen Balls - der Ball ist groesser und wirft einen versetzten Schatten. */
  lift?: number;
}): JSX.Element | null {
  const factor = elementScale(element, scale);
  switch (element.type) {
    case 'zone': {
      const color = element.color ?? 'white';
      const shape = element.shape ?? 'rect';
      // Weisses Rechteck: die Zone, wie sie immer aussah.
      if (color === 'white' && shape === 'rect') {
        return (
          <rect
            data-element="zone"
            x={element.x}
            y={(element.y / 100) * height}
            width={element.w}
            height={(element.h / 100) * height}
            fill="rgba(255,255,255,0.16)"
            stroke="rgba(255,255,255,0.75)"
            strokeWidth={0.5}
            strokeDasharray="1.6 1.2"
          />
        );
      }
      const style = ZONE_STYLE[color];
      return (
        <g data-element="zone" data-shape={shape} data-color={color}>
          <ZoneOutline zone={element} height={height} fill={style.fill} />
          {style.pattern && <ZoneOutline zone={element} height={height} fill={`url(#${markerId}${style.pattern})`} />}
          {/* Dunkler Schatten unter dem Rand: die Farbe hebt sich auch vom Rasen ab. */}
          {color !== 'white' && <ZoneOutline zone={element} height={height} fill="none" stroke="rgba(15,23,42,0.4)" strokeWidth={style.strokeWidth + 1.1} strokeLinejoin="round" />}
          <ZoneOutline
            zone={element}
            height={height}
            fill="none"
            stroke={style.stroke}
            strokeWidth={style.strokeWidth}
            strokeDasharray={style.dash}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      );
    }
    case 'text': {
      const layout = textLayout(element, height, scale);
      // Heller Text mit dunklem Halo (die Kontur liegt unter der Fuellung): lesbar auf Rasen, Linien und Spielern.
      return (
        <g data-element="text" data-text={element.text} data-width={Math.round(layout.w * 100) / 100} data-scale={factor === 1 ? undefined : factor}>
          <text
            x={layout.cx}
            y={layout.cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={layout.fontSize}
            fontWeight={700}
            fontFamily="system-ui, sans-serif"
            fill="#ffffff"
            stroke="rgba(15,23,42,0.92)"
            strokeWidth={Math.round(layout.fontSize * 0.3 * 100) / 100}
            strokeLinejoin="round"
            paintOrder="stroke"
          >
            {element.text}
          </text>
        </g>
      );
    }
    case 'goal':
      return <GoalShape x={element.x} y={(element.y / 100) * height} size={element.size} rotation={element.rotation ?? 0} factor={factor} />;
    case 'cone': {
      const cx = element.x;
      const cy = (element.y / 100) * height;
      return (
        <polygon
          data-element="cone"
          data-scale={factor === 1 ? undefined : factor}
          transform={scaleAbout(cx, cy, factor)}
          points={`${cx},${cy - 2} ${cx + 1.8},${cy + 1.5} ${cx - 1.8},${cy + 1.5}`}
          fill="#f97316"
          stroke="#ffffff"
          strokeWidth={0.4}
          strokeLinejoin="round"
        />
      );
    }
    case 'pole': {
      // Slalomstange von oben: kleiner Kreis mit kraeftigem dunklem Rand - viel kleiner als ein Spieler, dunkler als der Ball.
      const cx = element.x;
      const cy = (element.y / 100) * height;
      return (
        <g data-element="pole" data-scale={factor === 1 ? undefined : factor} transform={scaleAbout(cx, cy, factor)}>
          <circle cx={cx} cy={cy} r={1.05} fill="#fde047" stroke="#1f2937" strokeWidth={1.05} />
        </g>
      );
    }
    case 'ring': {
      // Koordinationsring: nur Kontur, etwa so gross wie ein Spieler; mit dunklem Schatten, damit er auf dem Rasen auch grau gedruckt steht.
      const cx = element.x;
      const cy = (element.y / 100) * height;
      return (
        <g data-element="ring" data-scale={factor === 1 ? undefined : factor} transform={scaleAbout(cx, cy, factor)} fill="none">
          <circle cx={cx} cy={cy} r={RING_RADIUS} stroke="#1f2937" strokeWidth={1.8} opacity={0.45} />
          <circle cx={cx} cy={cy} r={RING_RADIUS} stroke="#fb923c" strokeWidth={1} />
        </g>
      );
    }
    case 'hurdle': {
      // Huerde von oben: Balken mit zwei Fuessen quer dazu (wie ein liegendes H); die Drehung stellt sie quer zur Laufrichtung.
      const cx = element.x;
      const cy = (element.y / 100) * height;
      const rotation = element.rotation ?? 0;
      const shape = `M ${cx - 4.2} ${cy} H ${cx + 4.2} M ${cx - 3.4} ${cy - 1.9} V ${cy + 1.9} M ${cx + 3.4} ${cy - 1.9} V ${cy + 1.9}`;
      const scaled = scaleAbout(cx, cy, factor);
      return (
        <g
          data-element="hurdle"
          data-rotation={rotation}
          data-scale={scaled ? factor : undefined}
          transform={`rotate(${rotation} ${cx} ${cy})${scaled ? ` ${scaled}` : ''}`}
          fill="none"
          strokeLinecap="round"
        >
          <path d={shape} stroke="#1f2937" strokeWidth={2.4} opacity={0.45} />
          <path d={shape} stroke="#f8fafc" strokeWidth={1.3} />
        </g>
      );
    }
    case 'arrow':
      return <Arrow kind={element.kind} from={element.from} to={element.to} via={element.via} points={element.points} smooth={element.smooth} follow={element.follow} markerId={markerId} height={height} factor={arrowScale(scale)} />;
    case 'measure':
      return <MeasureLine from={element.from} to={element.to} label={element.label} height={height} factor={arrowScale(scale)} />;
    case 'player': {
      const style = TEAM_STYLE[element.team];
      const cx = element.x;
      const cy = (element.y / 100) * height;
      return (
        <g
          data-element="player"
          data-team={element.team}
          data-facing={element.facing}
          data-scale={factor === 1 ? undefined : factor}
          transform={scaleAbout(cx, cy, factor)}
        >
          {element.facing !== undefined && <FacingNose cx={cx} cy={cy} facing={element.facing} style={style} />}
          <circle cx={cx} cy={cy} r={PLAYER_RADIUS} fill={style.fill} {...rimProps(style)} />
          {element.label && (
            <text
              x={cx}
              y={cy}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={element.label.length > 2 ? 2.4 : 3.2}
              fontWeight={700}
              fontFamily="system-ui, sans-serif"
              fill={style.text}
            >
              {element.label}
            </text>
          )}
        </g>
      );
    }
    case 'ball': {
      const cx = element.x;
      const cy = (element.y / 100) * height;
      const ball = (grow: number): JSX.Element => (
        <circle
          data-element="ball"
          data-scale={factor * grow === 1 ? undefined : Math.round(factor * grow * 1000) / 1000}
          data-lift={grow === 1 ? undefined : Math.round(lift * 100) / 100}
          transform={scaleAbout(cx, cy, factor * grow)}
          cx={cx}
          cy={cy}
          r={1.5}
          fill="#ffffff"
          stroke="#111827"
          strokeWidth={0.5}
        />
      );
      if (!(lift > 0)) return ball(1);
      // Hoher Ball: je hoeher, desto groesser; der Schatten bleibt am Boden und rutscht nach rechts unten weg.
      return (
        <g data-lifted={Math.round(lift * 100) / 100}>
          <ellipse
            data-testid="ball-shadow"
            cx={cx + 0.4 + lift * 2.4 * factor}
            cy={cy + 0.5 + lift * 3 * factor}
            rx={1.5 * factor * (1 + lift * 0.25)}
            ry={0.95 * factor * (1 + lift * 0.25)}
            fill="rgba(15,23,42,0.4)"
          />
          {ball(1 + lift * 0.9)}
        </g>
      );
    }
  }
}

/** ViewBox einer Skizze: das Feld samt schmalem Rand. */
export function pitchViewBox(pitch: DiagramPitch): string {
  return `${-PADDING} ${-PADDING} ${100 + 2 * PADDING} ${PITCH_HEIGHT[pitch] + 2 * PADDING}`;
}

/** Eindeutige Kennung fuer die Pfeilspitzen-Markierungen einer Skizze. */
export function useMarkerId(): string {
  return `pitch${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
}

/**
 * Pfeilspitzen, Rasen und Feldlinien - der Untergrund, auf dem die Elemente liegen.
 * `scale` ist die Symbolgroesse der Skizze; die Pfeilspitzen folgen ihr maessig (`arrowScale`),
 * Rasen und Linien bleiben.
 */
export function PitchSurface({
  pitch,
  markerId,
  scale = 1,
  zoneColors = [],
}: {
  pitch: DiagramPitch;
  markerId: string;
  scale?: number;
  /** Farben der Zonen, die vorkommen (`zoneColorsOf`): nur ihre Fuellmuster kommen ins `<defs>` - Skizzen ohne farbige Zonen bleiben, wie sie waren. */
  zoneColors?: readonly DiagramZoneColor[];
}): JSX.Element {
  const height = PITCH_HEIGHT[pitch];
  const factor = arrowScale(scale);
  return (
    <>
      <defs>
        <marker id={markerId} viewBox="0 0 4 4" refX="3.4" refY="2" markerWidth={sc(4, factor)} markerHeight={sc(4, factor)} markerUnits="userSpaceOnUse" orient="auto">
          <polygon points="0,0 4,2 0,4" fill={ARROW} />
        </marker>
        <marker id={`${markerId}S`} viewBox="0 0 5 5" refX="4.2" refY="2.5" markerWidth={sc(5, factor)} markerHeight={sc(5, factor)} markerUnits="userSpaceOnUse" orient="auto">
          <polygon points="0,0 5,2.5 0,5" fill={ARROW} />
        </marker>
        {/* Positionswechsel: kleine offene Spitze (zwei Striche) statt der gefuellten. */}
        <marker id={`${markerId}W`} viewBox="0 0 5 5" refX="4.2" refY="2.5" markerWidth={sc(3.6, factor)} markerHeight={sc(3.6, factor)} markerUnits="userSpaceOnUse" orient="auto">
          <polyline points="0.8,0.7 4.2,2.5 0.8,4.3" fill="none" stroke={SWITCH_COLOR} strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
        </marker>
        {/* Fuellmuster der farbigen Zonen (siehe `ZONE_STYLE`): Gelb "/", Rot "\", Blau Punkte, Orange kariert - gedruckt in Grau noch zu unterscheiden. */}
        {zoneColors.includes('yellow') && (
          <pattern id={`${markerId}Zy`} patternUnits="userSpaceOnUse" width="2.6" height="2.6" patternTransform="rotate(45)">
            <path d="M0 0V2.6" stroke="rgba(254,249,195,0.9)" strokeWidth="0.55" />
          </pattern>
        )}
        {zoneColors.includes('red') && (
          <pattern id={`${markerId}Zr`} patternUnits="userSpaceOnUse" width="2.6" height="2.6" patternTransform="rotate(-45)">
            <path d="M0 0V2.6" stroke="rgba(254,202,202,0.85)" strokeWidth="0.55" />
          </pattern>
        )}
        {zoneColors.includes('blue') && (
          <pattern id={`${markerId}Zb`} patternUnits="userSpaceOnUse" width="2.8" height="2.8">
            <circle cx="1.4" cy="1.4" r="0.55" fill="rgba(219,234,254,0.9)" />
          </pattern>
        )}
        {zoneColors.includes('orange') && (
          <pattern id={`${markerId}Zo`} patternUnits="userSpaceOnUse" width="2.8" height="2.8" patternTransform="rotate(45)">
            <path d="M0 0V2.8M0 0H2.8" fill="none" stroke="rgba(255,237,213,0.85)" strokeWidth="0.45" />
          </pattern>
        )}
      </defs>
      <rect x={-PADDING} y={-PADDING} width={100 + 2 * PADDING} height={height + 2 * PADDING} rx={2} fill={GRASS_DARK} />
      <rect x={0} y={0} width={100} height={height} fill={GRASS} />
      {pitch !== 'AREA' && <PitchLines pitch={pitch} height={height} />}
      {pitch === 'AREA' && <rect x={0} y={0} width={100} height={height} fill="none" stroke={LINE} strokeWidth={0.6} />}
    </>
  );
}

/**
 * Kleine Schrittnummern neben den Pfeilanfaengen. Nur bei mehr als einem Schritt;
 * die Zahl sitzt seitlich versetzt, damit sie Spieler und Ball nicht verdeckt.
 */
export function StepNumbers({
  diagram,
  height,
  visible,
}: {
  diagram: TrainingDiagram;
  height: number;
  /** Nur fuer diese Pfeile (Index) Nummern zeigen - der Player blendet damit Nummern ausgeblendeter Pfeile aus. */
  visible?: (arrowIndex: number) => boolean;
}): JSX.Element | null {
  const steps = diagramSteps(diagram);
  if (steps.length < 2) return null;
  return (
    <g data-testid="step-numbers" pointerEvents="none">
      {steps.flatMap((step, stepIndex) =>
        step.arrows.map((index) => {
          const arrow = diagram.elements[index];
          if (arrow?.type !== 'arrow' || (visible && !visible(index))) return null;
          // Bei gebogenen Pfeilen zaehlt die Anfangsrichtung: sie zeigt zum Kontrollpunkt, bei Knickpfeilen zum ersten Knick.
          const aim = arrow.via ?? arrow.points?.[0] ?? arrow.to;
          const dx = aim[0] - arrow.from[0];
          const dy = ((aim[1] - arrow.from[1]) / 100) * height;
          const length = Math.hypot(dx, dy) || 1;
          // Senkrecht zur Pfeilrichtung, zur linken Seite des Pfeils.
          const cx = arrow.from[0] + (dy / length) * 3.6;
          const cy = (arrow.from[1] / 100) * height - (dx / length) * 3.6;
          // Ein Lauf ueber mehrere Schritte ("Laeuft bis Schritt") zeigt "1–2" in einer schmalen Kapsel statt der runden Marke.
          const endNumber = steps[arrowLastStepIndex(steps, stepIndex, arrow)]!.number;
          const span = endNumber > step.number;
          return (
            <g key={index} data-step={step.number} data-step-end={span ? endNumber : undefined}>
              {span ? (
                <rect x={cx - 2.9} y={cy - 2} width={5.8} height={4} rx={2} fill="rgba(15,23,42,0.78)" stroke="#ffffff" strokeWidth={0.35} />
              ) : (
                <circle cx={cx} cy={cy} r={2} fill="rgba(15,23,42,0.78)" stroke="#ffffff" strokeWidth={0.35} />
              )}
              <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={span ? 2.3 : 2.5} fontWeight={700} fontFamily="system-ui, sans-serif" fill="#ffffff">
                {span ? `${step.number}–${endNumber}` : step.number}
              </text>
            </g>
          );
        }),
      )}
    </g>
  );
}

export function PitchDiagram({
  diagram,
  className,
  title,
  showSteps = false,
}: {
  diagram: TrainingDiagram;
  className?: string;
  title?: string;
  /** Dezente Schrittnummern an den Pfeilanfaengen (nur im Detail; Listen und Druck zeigen sie nicht). */
  showSteps?: boolean;
}): JSX.Element {
  const markerId = useMarkerId();
  const height = PITCH_HEIGHT[diagram.pitch];
  const scale = diagram.symbolScale ?? 1;
  const elements = [...diagram.elements].sort((x, y) => LAYER[x.type] - LAYER[y.type]);

  return (
    <svg
      role="img"
      aria-label={title ? `${title}: ${describeDiagram(diagram)}` : describeDiagram(diagram)}
      viewBox={pitchViewBox(diagram.pitch)}
      className={cn('block h-auto w-full', className)}
      data-pitch={diagram.pitch}
      style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}
    >
      <PitchSurface pitch={diagram.pitch} markerId={markerId} scale={scale} zoneColors={zoneColorsOf(diagram.elements)} />
      {elements.map((element, index) => (
        <SketchElement key={index} element={element} markerId={markerId} height={height} scale={scale} />
      ))}
      {showSteps && <StepNumbers diagram={diagram} height={height} />}
    </svg>
  );
}

/** Kleine Legende: zeigt nur, was in der Skizze vorkommt. */
export function DiagramLegend({ diagram, className }: { diagram: TrainingDiagram; className?: string }): JSX.Element | null {
  const teams = DIAGRAM_TEAM.filter((team) => diagram.elements.some((element) => element.type === 'player' && element.team === team));
  const arrows = (['PASS', 'RUN', 'DRIBBLE', 'SHOT', 'LOFT', 'SWITCH'] as const).filter((kind) => diagram.elements.some((element) => element.type === 'arrow' && element.kind === kind));
  const hasFollow = diagram.elements.some((element) => element.type === 'arrow' && element.kind === 'PASS' && element.follow === true);
  const hasCone = diagram.elements.some((element) => element.type === 'cone');
  const equipment = (['pole', 'ring', 'hurdle'] as const).filter((type) => diagram.elements.some((element) => element.type === type));
  const hasMeasure = diagram.elements.some((element) => element.type === 'measure');
  const hasFacing = diagram.elements.some((element) => element.type === 'player' && element.facing !== undefined);
  if (teams.length === 0 && arrows.length === 0 && !hasFollow && !hasCone && equipment.length === 0 && !hasMeasure && !hasFacing) return null;

  return (
    <ul
      aria-label="Legende"
      className={cn('flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600', className)}
      style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}
    >
      {teams.map((team) => (
        <li key={team} className="flex items-center gap-1.5">
          <TeamSwatch team={team} className="h-4 w-4 shrink-0" />
          {TEAM_STYLE[team].name}
        </li>
      ))}
      {hasCone && (
        <li className="flex items-center gap-1.5">
          <svg viewBox="0 0 10 10" className="h-3 w-3" aria-hidden="true"><polygon points="5,1 9,9 1,9" fill="#f97316" /></svg>
          Hütchen
        </li>
      )}
      {equipment.map((type) => (
        <li key={type} className="flex items-center gap-1.5">
          {/* Auf Rasengruen, wie in der Skizze: die Ausstattung ist hell und liegt auf dem Feld. */}
          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 rounded-sm" style={{ backgroundColor: GRASS }} aria-hidden="true">
            {type === 'pole' && <circle cx="12" cy="12" r="4.2" fill="#fde047" stroke="#1f2937" strokeWidth="4.2" />}
            {type === 'ring' && (
              <>
                <circle cx="12" cy="12" r="7.6" fill="none" stroke="#1f2937" strokeWidth="3.4" opacity="0.45" />
                <circle cx="12" cy="12" r="7.6" fill="none" stroke="#fb923c" strokeWidth="2" />
              </>
            )}
            {type === 'hurdle' && (
              <>
                <path d="M4 12H20M5.5 6.5V17.5M18.5 6.5V17.5" fill="none" stroke="#1f2937" strokeWidth="4" strokeLinecap="round" opacity="0.45" />
                <path d="M4 12H20M5.5 6.5V17.5M18.5 6.5V17.5" fill="none" stroke="#f8fafc" strokeWidth="2.4" strokeLinecap="round" />
              </>
            )}
          </svg>
          {EQUIPMENT_LABELS[type]}
        </li>
      ))}
      {arrows.map((kind) => (
        <li key={kind} className="flex items-center gap-1.5">
          <svg viewBox="0 0 24 8" className="h-2 w-6 rounded-sm" style={{ backgroundColor: GRASS }} aria-hidden="true">
            {kind === 'PASS' && <line x1="1" y1="4" x2="23" y2="4" stroke={ARROW} strokeWidth="1.4" />}
            {kind === 'RUN' && <line x1="1" y1="4" x2="23" y2="4" stroke={ARROW} strokeWidth="1.4" strokeDasharray="3 2" />}
            {kind === 'DRIBBLE' && <path d="M1 4 Q4 0 7 4 T13 4 T19 4 T23 4" fill="none" stroke={ARROW} strokeWidth="1.2" />}
            {kind === 'SHOT' && (
              <>
                <line x1="1" y1="4" x2="23" y2="4" stroke={ARROW} strokeWidth="3.4" />
                <line x1="1" y1="4" x2="23" y2="4" stroke={GRASS} strokeWidth="1.2" />
              </>
            )}
            {kind === 'LOFT' && (
              <>
                <line x1="1" y1="5.4" x2="23" y2="5.4" stroke={ARROW} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="3 1.2 0.01 1.2" />
                <path d="M8 6 Q12 -1 16 6" fill="none" stroke={ARROW} strokeWidth="1.1" strokeLinecap="round" />
              </>
            )}
            {kind === 'SWITCH' && (
              <>
                <line x1="1" y1="4" x2="19" y2="4" stroke={SWITCH_COLOR} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="0.01 2.4" />
                <path d="M18 1.6L22 4L18 6.4" fill="none" stroke={SWITCH_COLOR} strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
              </>
            )}
          </svg>
          {ARROW_LABELS[kind]}
        </li>
      ))}
      {hasFollow && (
        <li className="flex items-center gap-1.5">
          <svg viewBox="0 0 24 8" className="h-2 w-6 rounded-sm" style={{ backgroundColor: GRASS }} aria-hidden="true">
            <line x1="1" y1="2.4" x2="23" y2="2.4" stroke={ARROW} strokeWidth="1.1" />
            <line x1="1" y1="5.8" x2="19" y2="5.8" stroke={SWITCH_COLOR} strokeWidth="1.1" strokeLinecap="round" strokeDasharray="0.01 2.4" />
            <path d="M18 3.9L22 5.8L18 7.7" fill="none" stroke={SWITCH_COLOR} strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {FOLLOW_LABEL}
        </li>
      )}
      {hasFacing && (
        <li className="flex items-center gap-1.5">
          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 rounded-sm" style={{ backgroundColor: GRASS }} aria-hidden="true">
            <polygon points="12,1.6 15.6,8.4 8.4,8.4" fill="#ffffff" stroke="#111827" strokeWidth="1" strokeLinejoin="round" />
            <circle cx="12" cy="14" r="6.4" fill="#dc2626" stroke="#ffffff" strokeWidth="1.4" />
          </svg>
          {FACING_LABEL}
        </li>
      )}
      {hasMeasure && (
        <li className="flex items-center gap-1.5">
          <svg viewBox="0 0 24 8" className="h-2 w-6 rounded-sm" style={{ backgroundColor: GRASS }} aria-hidden="true">
            <path d="M2 4H22M2 1.5V6.5M22 1.5V6.5" fill="none" stroke={MEASURE_COLOR} strokeWidth="1" strokeLinecap="round" />
          </svg>
          {MEASURE_LABEL}
        </li>
      )}
    </ul>
  );
}

